import React from "react";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useQuery } from "@tanstack/react-query";
import { archiveReasons } from "../common/dictionaries/archiveReasons";

import { FaRegCalendarAlt, FaRegEye } from "react-icons/fa";
import { BiSolidEditAlt } from "react-icons/bi";
import { MdPrint } from "react-icons/md";
import { useTranslation } from "react-i18next";

const groupActionsByDate = (actionsData) => {
  const grouped = {};

  Object.keys(actionsData).forEach((actionType) => {
    Object.entries(actionsData[actionType]).forEach(([userId, userActions]) => {
      userActions.forEach((action) => {
        const date = action.datetime.split("T")[0];
        const time = action.datetime.split("T")[1];
        const minutesSinceMidnight =
          parseInt(time.split(":")[0]) * 60 + parseInt(time.split(":")[1]);
        const intervalId = Math.floor(minutesSinceMidnight / 5);

        if (!grouped[date]) {
          grouped[date] = { readIntervals: {}, otherActions: [] };
        }

        if (actionType === "read") {
          if (!grouped[date].readIntervals[intervalId]) {
            grouped[date].readIntervals[intervalId] = {};
          }
          if (!grouped[date].readIntervals[intervalId][userId]) {
            grouped[date].readIntervals[intervalId][userId] = [];
          }
          grouped[date].readIntervals[intervalId][userId].push({
            ...action,
            type: actionType,
            userId,
          });
        } else {
          grouped[date].otherActions.push({
            ...action,
            type: actionType,
            userId,
          });
        }
      });
    });
  });

  const result = {};
  Object.keys(grouped).forEach((date) => {
    result[date] = [];
    // Add read actions
    Object.entries(grouped[date].readIntervals).forEach(
      ([intervalId, usersActions]) => {
        Object.values(usersActions).forEach((userActions) => {
          if (userActions.length > 0) {
            const combinedAction = {
              datetime: userActions[0].datetime,
              type: "read",
              userId: userActions[0].userId,
              details: userActions,
            };
            result[date].push(combinedAction);
          }
        });
      },
    );

    // Add other actions
    result[date].push(...grouped[date].otherActions);

    // Sort actions chronologically by date and time
    result[date].sort((a, b) => b.datetime.localeCompare(a.datetime));
  });

  return result;
};


const UserActionsFeed = ({ dataForm }) => {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const actions = dataForm?.process?.orderInteractionHistory;
  const groupedActions = actions ? groupActionsByDate(actions) : {};
console.log(`actions`, actions);
  const sortedDates = Object.keys(groupedActions).sort((a, b) =>
    b.localeCompare(a),
  );
  
  const getArchiveReasonLabel = (value) => {
    const reasonObject = archiveReasons.find((reason) => reason.value === value);
    return reasonObject ? t(reasonObject.label) : t("unknown_reason");
  };

  const formatDateTime = (datetime, type) => {
    const date = new Date(`${datetime}Z`);
    const timeOptions = {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Paris",
    };
    const dateOptions = {
      day: "numeric",
      month: "long",
      timeZone: "Europe/Paris",
    };

    switch (type) {
      case "time":
        return date.toLocaleTimeString([], timeOptions);
      case "datetime":
        return `${date.toLocaleDateString(
          "fr-FR",
          dateOptions
        )} ${date.toLocaleTimeString([], timeOptions)}`;
      case "date":
        return date.toLocaleDateString("fr-FR", dateOptions);
      default:
        return datetime;
    }
  };

  const { data: usersData } = useQuery({
    queryKey: ["AllUsers"],
    queryFn: async () => {
      const response = await axiosPrivate.get(`/users`);
      return response.data;
    },
    staleTime: 6000000,
    gcTime: 6000000,
  });

  const getUserFullName = (userId) => {
    if (userId === "system" || userId === "automatic") {
      return t("system_notification");
    }
    const user = usersData?.data.find((user) => user.user_id === userId);
    return user
      ? `${user.user_given_name} ${user.user_name}`
      : t("unknown_user");
  };

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case "record":
        return <BiSolidEditAlt className="text-large mr-2" />;
      case "print":
        return <MdPrint className="text-large mr-2" />;
      default:
        return <FaRegEye className="text-large mr-2" />;
    }
  };

  const getActionLabel = (actionType) => {
    switch (actionType) {
      case "record":
        return t("modification");
      case "print":
        return t("printing");
      default:
        return t("reading");
    }
  };

  return (
    <div className="bg-white rounded-xl">
      <div className="border-b-1 border-vistagrey/30 px-3 pt-1 pb-3 text-xs font-bold">
        {t("user_actions_history")}
      </div>
      <div className="flex flex-col px-8">
        {sortedDates.map((date) => (
          <div key={date}>
            <div className="flex text-large text-vistagrey py-6">
              <FaRegCalendarAlt className="text-2xl pr-2" />
              {formatDateTime(date, "date")}
            </div>
            {groupedActions[date].map((action, index) => (
              <React.Fragment key={index}>
                {action.type === "read" || action.type === "record" || action.type === "print" ? (
                  <div
                    key={index}
                    className={`${
                      index === groupedActions[date].length - 1
                        ? ""
                        : "border-l-2"
                    } pb-6 text-vistadark text-xs flex border-gray-300 relative pl-5 ml-7 before:content-[''] before:w-4 before:h-4 before:bg-vistagreen before:text-grey-500 before:rounded-full before:absolute before:-left-[0.55rem] before:top-0`}
                  >
                    <div className="flex">
                      {formatDateTime(action.datetime, "time")}
                    </div>
                    <div className="ml-12">
                      <div className="flex text-vistagreen uppercase font-bold">
                        {getActionIcon(action.type)}{" "}
                        {getActionLabel(action.type)}
                      </div>
                      <div className="ml-[26px] pt-1">
                        {getUserFullName(action.userId)}
                      </div>
                    </div>
                  </div>
                ) : null}

                {action.type === "archive" ? (
                  <div className="bg-violet-200 rounded-lg p-4 text-sm mt-4 mb-6 w-[90%] ml-3">
                    <div className="border-l-4 border-violet-800 pl-6">
                      <div className="flex">
                        <div className="w-28 font-bold">{t("actions")}</div>
                        <div>{getArchiveReasonLabel(action.reason)}</div>
                      </div>
                      <div className="flex pt-3">
                        <div className="w-28 font-bold">{t("reason")}</div>
                        <div>{action.comment}</div>
                      </div>
                      <div className="flex pt-3">
                        <div className="w-28 font-bold">{t("user")}</div>
                        <div>{getUserFullName(action.userId)}</div>
                      </div>
                      <div className="flex pt-3">
                        <div className="w-28 font-bold">{t("date")}</div>
                        <div>{formatDateTime(action.datetime, "datetime")}</div>
                      </div>
                    </div>
                  </div>
                ) : null}

                {action.type === "failed" ? (
                  <div className=" bg-vistactared rounded-lg p-4 text-sm mt-4 mb-6 w-[90%] ml-3 text-white">
                    <div className="border-l-4 border-white/40 pl-6">
                      <div className="flex">
                        <div className="w-28 font-bold">{t("reason")}</div>
                        <div>{action.comment}
                          {action.reason && action.reason !== action.comment && (
                            <span>: {action.reason}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex pt-3">
                        <div className="w-28 font-bold">{t("user")}</div>
                        <div>{getUserFullName(action.userId)}</div>
                      </div>
                      <div className="flex pt-3">
                        <div className="w-28 font-bold">{t("date")}</div>
                        <div>{formatDateTime(action.datetime, "datetime")}</div>
                      </div>
                    </div>
                  </div>
                ) : null}

                {action.type === "validate" ? (
                  <div className="bg-vistagreen/40 rounded-lg p-4 text-sm mt-4 mb-6 w-[90%] ml-3">
                    <div className="border-l-4 border-vistagreen pl-6">
                      <div className="flex">
                        <div className="w-28 font-bold">{t("actions")}</div>
                        <div>
                          {action.userId === "automatic"
                            ? t("order_validated_automatically")
                            : t("order_validated")}
                        </div>
                      </div>
                      <div className="flex pt-3">
                        <div className="w-28 font-bold">{t("user")}</div>
                        <div>{getUserFullName(action.userId)}</div>
                      </div>
                      <div className="flex pt-3">
                        <div className="w-28 font-bold">{t("date")}</div>
                        <div>{formatDateTime(action.datetime, "datetime")}</div>
                      </div>
                    </div>
                  </div>
                ) : null}

                {action.type === "assignment" ? (
                  <div className="bg-vistagrey/40 rounded-lg p-4 text-sm mt-4 mb-6 w-[90%] ml-3">
                    <div className="border-l-4 border-vistagrey pl-6">
                      <div className="flex">
                        <div className="w-28 font-bold">{t("actions")}</div>
                        <div>{t("assignment_modification")}</div>
                      </div>
                      <div className="flex pt-3">
                        <div className="w-28 font-bold">{t("user")}</div>
                        <div>{getUserFullName(action.userId)}</div>
                      </div>
                      <div className="flex pt-3">
                        <div className="w-28 font-bold">{t("assignment")}</div>
                        <div>
                          {t("order_assigned_to")}{" "}
                          {getUserFullName(action.comment)}
                        </div>
                      </div>
                      <div className="flex pt-3">
                        <div className="w-28 font-bold">{t("date")}</div>
                        <div>{formatDateTime(action.datetime, "datetime")}</div>
                      </div>
                    </div>
                  </div>
                ) : null}

                {action.type === "product_added" ? (
                  <div className="bg-vistablue/30 rounded-lg p-4 text-sm mt-4 mb-6 w-[90%] ml-3">
                    <div className="border-l-4 border-vistablue pl-6">
                      <div className="flex">
                        <div className="w-28 font-bold">{t("actions")}</div>
                        <div>{t("product_added")}</div>
                      </div>
                      <div className="flex pt-3">
                        <div className="w-28 font-bold">{t("user")}</div>
                        <div>{getUserFullName(action.userId)}</div>
                      </div>
                      <div className="flex pt-3">
                        <div className="w-28 font-bold">{t("date")}</div>
                        <div>{formatDateTime(action.datetime, "datetime")}</div>
                      </div>
                      <div className="flex pt-3">
                        <div className="min-w-28 font-bold uppercase">{t("details")}</div>
                        <div>{action.comment}</div>
                      </div>
                    </div>
                  </div>
                ) : null}

                {action.type === "product_deleted" ? (
                  <div className="bg-vistactaorange/30 rounded-lg p-4 text-sm mt-4 mb-6 w-[90%] ml-3">
                    <div className="border-l-4 border-vistactaorange pl-6">
                      <div className="flex">
                        <div className="w-28 font-bold">{t("actions")}</div>
                        <div>{t("product_deleted")}</div>
                      </div>
                      <div className="flex pt-3">
                        <div className="w-28 font-bold">{t("user")}</div>
                        <div>{getUserFullName(action.userId)}</div>
                      </div>
                      <div className="flex pt-3">
                        <div className="w-28 font-bold">{t("date")}</div>
                        <div>{formatDateTime(action.datetime, "datetime")}</div>
                      </div>
                      <div className="flex pt-3">
                        <div className="min-w-28 font-bold uppercase">{t("details")}</div>
                        <div>{action.comment}</div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </React.Fragment>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default UserActionsFeed;